import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { IPaginatedResponse } from "../models/Pagination";
import {
  DhlTrackingResponse,
  FedexTrackingResponse,
  IAwb,
  IAwbAddStatus,
  IAwbDetail,
  ICreateShipmentInfo,
  IFilterAwb,
  IParamFilterShipment,
  IShipmentInfo,
  IUpdateShipmentInfo,
} from "../models/Tracking";
import {
  AwbService,
  FedexTrackingService,
  ShipmentService,
  TrackingService,
} from "../services/trackingService";
import { trackingQueryKey } from "./QueryKey";

export function useTrackDhlShipment(
  trackingNumber: string
): UseQueryResult<DhlTrackingResponse> {
  return useQuery({
    queryKey: [trackingQueryKey.DHL_TRACKING, trackingNumber],
    queryFn: async () => TrackingService.tracking(trackingNumber),
    enabled: !!trackingNumber,
  });
}
export function useTrackDhlShipments(
  trackingNumbers: string[]
): UseQueryResult<DhlTrackingResponse[]> {
  return useQuery({
    queryKey: [trackingQueryKey.DHL_TRACKINGS, trackingNumbers],
    queryFn: async () => {
      const results = await TrackingService.trackings(trackingNumbers);
      return results;
    },
    enabled: trackingNumbers.length > 0,
  });
}
export function useTrackFedexShipment(
  trackingNumber: string
): UseQueryResult<FedexTrackingResponse> {
  return useQuery({
    queryKey: [trackingQueryKey.FEDEX_TRACKING, trackingNumber],
    queryFn: async () => FedexTrackingService.tracking(trackingNumber),
    enabled: !!trackingNumber,
  });
}
export function useShipmentInfo(
  params: IParamFilterShipment
): UseQueryResult<IPaginatedResponse<IShipmentInfo>> {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(
      ([_, value]) => value !== "" && value !== null && value !== undefined
    )
  ) as IParamFilterShipment;

  return useQuery({
    queryKey: [trackingQueryKey.SHIPMENTS_INFO, cleanParams],
    queryFn: async () => ShipmentService.shipmentInfo(cleanParams),
  });
}
export function useCreateShipmentInfo(
  params: IParamFilterShipment
): UseMutationResult<IShipmentInfo[], unknown, ICreateShipmentInfo[], unknown> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ICreateShipmentInfo[]) =>
      ShipmentService.createShipment(data),
    onSuccess: (data) => {
      queryClient.setQueriesData(
        { queryKey: [trackingQueryKey.SHIPMENTS_INFO, params] },
        (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: [...(oldData.data || []), ...data],
          };
        }
      );
    },
  });
}
export function useUpdateShipmentInfo(): UseMutationResult<
  IShipmentInfo,
  unknown,
  IUpdateShipmentInfo,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: IUpdateShipmentInfo) =>
      ShipmentService.updateShipment(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [trackingQueryKey.SHIPMENTS_INFO],
        predicate: (query) =>
          query.queryKey[0] === trackingQueryKey.SHIPMENTS_INFO,
      });
    },
  });
}
export function useGetAwb(
  params: IFilterAwb
): UseQueryResult<IPaginatedResponse<IAwb>> {
  return useQuery({
    queryKey: [trackingQueryKey.AWB, params],
    queryFn: async () => AwbService.getAwb(params),
    enabled: !!params.page && !!params.limit,
  });
}
export function useGetAwbDetail(awbId: number): UseQueryResult<IAwbDetail> {
  return useQuery({
    queryKey: [trackingQueryKey.AWB_DETAIL, awbId],
    queryFn: async () => AwbService.getAwbById(awbId),
    enabled: !!awbId,
  });
}
export function useGetAwbDetailByCode(
  awbCode: string
): UseQueryResult<IAwbDetail> {
  return useQuery({
    queryKey: [trackingQueryKey.AWB_DETAIL, awbCode],
    queryFn: async () => AwbService.getAwbByCode(awbCode),
    enabled: !!awbCode,
  });
}
export function useAddAwbStatus(): UseMutationResult<
  IAwbDetail,
  unknown,
  IAwbAddStatus,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: IAwbAddStatus) => AwbService.addAwbStatus(data),
    onSuccess: (data) => {
      queryClient.setQueriesData(
        { queryKey: [trackingQueryKey.AWB_DETAIL, data.id] },
        data
      );
      queryClient.invalidateQueries({
        queryKey: [trackingQueryKey.AWB],
        predicate: (query) => query.queryKey[0] === trackingQueryKey.AWB,
      });
    },
  });
}
export function useUpdateAwbStatus(): UseMutationResult<
  IAwbDetail,
  unknown,
  string,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (awbCode: string) => AwbService.updateAwbStatus(awbCode),
    onSuccess: (data) => {
      queryClient.setQueriesData(
        { queryKey: [trackingQueryKey.AWB_DETAIL, data.id] },
        data
      );
      queryClient.invalidateQueries({
        queryKey: [trackingQueryKey.AWB],
        predicate: (query) => query.queryKey[0] === trackingQueryKey.AWB,
      });
    },
  });
}
